import { Inject, Injectable } from "@angular/core";
import { finalize, mergeMap as _observableMergeMap } from "rxjs/operators";
import { HttpClient, HttpHeaders, HttpResponse, HttpResponseBase } from "@angular/common/http";
import { Observable, of as _observableOf } from "rxjs";
import { throwError as _observableThrow } from "rxjs/internal/observable/throwError";
import { catchError as _observableCatch } from "rxjs/internal/operators/catchError";
import { LEGACY_API_BASE_URL } from "@api";

export interface IIcd {
    subCategoryId: string | undefined;
    title: string | undefined;
    classification: string | undefined;
    genderRestriction: string | undefined;
    causeDeath: string | undefined;
    description: string | undefined;
    reference: string | undefined;
    excluded: string | undefined;
}

@Injectable()
export class Cid10Service {
    private jsonParseReviver: ((key: string, value: any) => any) | undefined = undefined;

    constructor(private _http: HttpClient, @Inject(LEGACY_API_BASE_URL) private _url?: string) {}

    search(keyword: string | undefined): Observable<IIcd[]> {
        return new Observable<IIcd[]>((observer) => {
            if (keyword) {
                let url_ = this._url + "/api/services/app/NoteService/GetIcd";
                if (keyword) {
                    url_ += "?keyword=" + keyword;
                }

                let options_: any = {
                    observe: "response",
                    responseType: "blob",
                    headers: new HttpHeaders({}),
                };

                let searchObservable = this._http
                    .request("get", url_, options_)
                    .pipe(
                        _observableMergeMap((response_: any) => {
                            return this.processGetIcd(response_);
                        })
                    )
                    .pipe(
                        _observableCatch((response_: any) => {
                            if (response_ instanceof HttpResponseBase) {
                                try {
                                    return this.processGetIcd(response_ as any);
                                } catch (e) {
                                    return _observableThrow(e) as any as Observable<IIcd[]>;
                                }
                            } else return _observableThrow(response_) as any as Observable<IIcd[]>;
                        })
                    );

                searchObservable
                    .pipe(
                        finalize(() => {
                            observer.complete();
                        })
                    )
                    .subscribe((result) => {
                        observer.next(result);
                    });
            } else {
                observer.next([]);
                observer.complete();
            }
        });
    }

    processGetIcd(response: HttpResponseBase): Observable<IIcd[]> {
        const status = response.status;
        const responseBlob =
            response instanceof HttpResponse
                ? response.body
                : (<any>response).error instanceof Blob
                ? (<any>response).error
                : undefined;

        let _headers: any = {};
        if (response.headers) {
            for (let key of response.headers.keys()) {
                _headers[key] = response.headers.get(key);
            }
        }
        if (status === 200) {
            return blobToText(responseBlob).pipe(
                _observableMergeMap((_responseText) => {
                    let result200: any = null;
                    let resultData200 = _responseText === "" ? null : JSON.parse(_responseText, this.jsonParseReviver);

                    if (Array.isArray(resultData200)) {
                        result200 = [] as any;

                        for (let item of resultData200) result200.push(item);
                    } else {
                        result200 = <any>null;
                    }
                    return _observableOf(result200);
                })
            );
        } else if (status !== 200 && status !== 204) {
            return blobToText(responseBlob).pipe(
                _observableMergeMap((_responseText) => {
                    return throwException("An unexpected server error occurred.", status, _responseText, _headers);
                })
            );
        }
        return _observableOf<IIcd[]>([]);
    }
}

export class ApiException extends Error {
    message: string;
    status: number;
    response: string;
    headers: { [key: string]: any };
    result: any;

    constructor(message: string, status: number, response: string, headers: { [key: string]: any }, result: any) {
        super();

        this.message = message;
        this.status = status;
        this.response = response;
        this.headers = headers;
        this.result = result;
    }

    protected isApiException = true;

    static isApiException(obj: any): obj is ApiException {
        return obj.isApiException === true;
    }
}

function throwException(
    message: string,
    status: number,
    response: string,
    headers: { [key: string]: any },
    result?: any
): Observable<any> {
    if (result !== null && result !== undefined) return _observableThrow(result);
    else return _observableThrow(new ApiException(message, status, response, headers, null));
}

function blobToText(blob: any): Observable<string> {
    return new Observable<string>((observer: any) => {
        if (!blob) {
            observer.next("");
            observer.complete();
        } else {
            let reader = new FileReader();
            reader.onload = (event) => {
                observer.next((<any>event.target).result);
                observer.complete();
            };
            reader.readAsText(blob);
        }
    });
}
